import React, { useState } from 'react';
import { ArrowRight, ShieldCheck, CheckCircle2, Loader2 } from 'lucide-react';
import { submitEligibilityForm } from '../mock';
import { useToast } from '../hooks/use-toast';
import { getUtm, getPageContext } from '../lib/utm';
import { programs } from '../data/programsData';
import { useNavigate } from 'react-router-dom';

/**
 * Compact inline lead form used inside country study pages (hero + final CTA).
 * - Glass-style card
 * - Only 4 essential fields to maximise conversions
 * - Posts to the same /api/eligibility backend used by EligibilityModal
 * - variant: "hero" (dark bg over gradient) | "cta" (dark bg) | "light" (on white)
 */
const CountryLeadForm = ({
  country,
  countrySlug,
  source,
  accent = '#F4C542',
  primary = '#2B5B8A',
  dark = '#0A1650',
  variant = 'hero',
  compact = false,
  showCountrySelector = false,
  countryOptions = [],
  headline,
  subheadline,
}) => {
  const { toast } = useToast();
  const navigate = useNavigate();

  const initial = {
    name: '',
    phone: '',
    email: '',
    qualification: '',
    course: '',
    countrySelect: '',
  };

  const [data, setData] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  const handle = (e) => setData({ ...data, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...data,
        country: countrySlug || data.countrySelect || 'not-sure',
        source: source || `patricks-website:country-inline`,
        ...getUtm(),
        ...getPageContext(),
      };
      const res = await submitEligibilityForm(payload);
      if (res.success) {
        toast({
          title: 'Request received',
          description: 'A senior counsellor will call you within 24 hours.',
        });
        setData(initial);
        navigate('/thank-you');
      } else {
        toast({
          title: 'Could not submit',
          description: res.message || 'Please try again in a moment.',
          variant: 'destructive',
        });
      }
    } catch (err) {
      toast({
        title: 'Network error',
        description: 'Please try again or WhatsApp us.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  // ————————————————————————————————————————————————————————————
  // Styling per variant
  // ————————————————————————————————————————————————————————————
  const isLight = variant === 'light';
  const cardBg = isLight
    ? 'bg-white border border-gray-200 shadow-2xl'
    : 'bg-white/[0.08] backdrop-blur-xl border border-white/20 shadow-2xl';
  const labelColor = isLight ? 'text-[#4A5B6C]' : 'text-white/80';
  const inputBg = isLight
    ? 'bg-white border border-gray-200 text-[#1A2B3C] placeholder:text-gray-400 focus:border-[#2B5B8A]'
    : 'bg-white/[0.06] border border-white/20 text-white placeholder:text-white/50 focus:border-white/60';
  const headingColor = isLight ? 'text-[#1A2B3C]' : 'text-white';
  const subColor = isLight ? 'text-[#4A5B6C]' : 'text-white/70';
  const badgeBg = isLight
    ? 'bg-[#F4F7FB] text-[#2B5B8A]'
    : 'bg-white/10 text-white';

  return (
    <div
      data-testid={`country-lead-form-${variant}`}
      className={`${cardBg} rounded-3xl p-6 sm:p-7 relative overflow-hidden`}
    >
      {/* Accent ribbon */}
      <div
        className="absolute -top-1 left-6 right-6 h-1 rounded-b-full"
        style={{ background: accent }}
      />

      {/* Header */}
      <div className="mb-4">
        <div
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider mb-2 ${badgeBg}`}
        >
          <ShieldCheck size={11} /> Free Profile Assessment
        </div>
        <h3
          className={`text-xl sm:text-2xl font-extrabold ${headingColor} leading-tight`}
          style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
        >
          {headline || `Talk to a ${country} Admissions Expert`}
        </h3>
        <p className={`text-xs sm:text-sm ${subColor} mt-1`}>
          {subheadline || 'Get a personalised university shortlist & cost estimate within 24 hours.'}
        </p>
      </div>

      <form onSubmit={submit} className="space-y-3">
        <div>
          <label className={`block text-[11px] font-semibold ${labelColor} mb-1`}>
            Full name
          </label>
          <input
            data-testid={`lead-form-name-${variant}`}
            type="text"
            name="name"
            required
            value={data.name}
            onChange={handle}
            placeholder="Your full name"
            className={`w-full px-3.5 py-2.5 rounded-xl text-sm ${inputBg} focus:outline-none focus:ring-2 focus:ring-white/10 transition-all`}
          />
        </div>

        {showCountrySelector && (
          <div>
            <label className={`block text-[11px] font-semibold ${labelColor} mb-1`}>
              Preferred destination
            </label>
            <select
              data-testid={`lead-form-country-${variant}`}
              name="countrySelect"
              required
              value={data.countrySelect}
              onChange={handle}
              className={`w-full px-3 py-2.5 rounded-xl text-sm ${inputBg} focus:outline-none focus:ring-2 focus:ring-white/10 transition-all appearance-none`}
              style={{
                backgroundImage:
                  variant === 'light'
                    ? "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%234A5B6C' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E\")"
                    : "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23ffffff' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E\")",
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 0.85rem center',
                paddingRight: '2.25rem',
              }}
            >
              <option value="" className="text-[#1A2B3C]">Select destination…</option>
              {countryOptions.map((c) => (
                <option key={c.slug} value={c.slug} className="text-[#1A2B3C]">
                  {c.flag} {c.name}
                </option>
              ))}
              <option value="not-sure" className="text-[#1A2B3C]">Not sure yet — recommend for me</option>
            </select>
          </div>
        )}

        <div className={compact ? '' : 'grid sm:grid-cols-2 gap-3'}>
          <div>
            <label className={`block text-[11px] font-semibold ${labelColor} mb-1`}>
              Phone (WhatsApp)
            </label>
            <input
              data-testid={`lead-form-phone-${variant}`}
              type="tel"
              name="phone"
              required
              value={data.phone}
              onChange={handle}
              placeholder="+91 98xxxxxx"
              className={`w-full px-3.5 py-2.5 rounded-xl text-sm ${inputBg} focus:outline-none focus:ring-2 focus:ring-white/10 transition-all`}
            />
          </div>
          <div>
            <label className={`block text-[11px] font-semibold ${labelColor} mb-1`}>
              Email
            </label>
            <input
              data-testid={`lead-form-email-${variant}`}
              type="email"
              name="email"
              required
              value={data.email}
              onChange={handle}
              placeholder="you@example.com"
              className={`w-full px-3.5 py-2.5 rounded-xl text-sm ${inputBg} focus:outline-none focus:ring-2 focus:ring-white/10 transition-all`}
            />
          </div>
        </div>

        <div className={compact ? '' : 'grid sm:grid-cols-2 gap-3'}>
          <div>
            <label className={`block text-[11px] font-semibold ${labelColor} mb-1`}>
              Highest qualification
            </label>
            <select
              data-testid={`lead-form-qualification-${variant}`}
              name="qualification"
              required
              value={data.qualification}
              onChange={handle}
              className={`w-full px-3 py-2.5 rounded-xl text-sm ${inputBg} focus:outline-none focus:ring-2 focus:ring-white/10 transition-all appearance-none`}
              style={{
                backgroundImage:
                  variant === 'light'
                    ? "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%234A5B6C' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E\")"
                    : "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23ffffff' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E\")",
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 0.85rem center',
                paddingRight: '2.25rem',
              }}
            >
              <option value="" className="text-[#1A2B3C]">Select…</option>
              <option value="high-school" className="text-[#1A2B3C]">12th / Higher Secondary</option>
              <option value="bachelors" className="text-[#1A2B3C]">Bachelor&apos;s</option>
              <option value="masters" className="text-[#1A2B3C]">Master&apos;s</option>
              <option value="other" className="text-[#1A2B3C]">Other</option>
            </select>
          </div>

          <div>
            <label className={`block text-[11px] font-semibold ${labelColor} mb-1`}>
              Program of interest
            </label>
            <select
              data-testid={`lead-form-course-${variant}`}
              name="course"
              required
              value={data.course}
              onChange={handle}
              className={`w-full px-3 py-2.5 rounded-xl text-sm ${inputBg} focus:outline-none focus:ring-2 focus:ring-white/10 transition-all appearance-none`}
              style={{
                backgroundImage:
                  variant === 'light'
                    ? "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%234A5B6C' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E\")"
                    : "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23ffffff' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E\")",
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 0.85rem center',
                paddingRight: '2.25rem',
              }}
            >
              <option value="" className="text-[#1A2B3C]">Select program…</option>
              {programs.map((p) => (
                <option key={p.slug} value={p.slug} className="text-[#1A2B3C]">
                  {p.name}
                </option>
              ))}
              <option value="other" className="text-[#1A2B3C]">Other / Not sure</option>
            </select>
          </div>
        </div>

        <button
          data-testid={`lead-form-submit-${variant}`}
          type="submit"
          disabled={submitting}
          className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-bold text-sm sm:text-base shadow-xl hover:-translate-y-0.5 transition disabled:opacity-70 disabled:cursor-not-allowed"
          style={{
            background: accent,
            color: '#0A1122',
          }}
        >
          {submitting ? (
            <>
              <Loader2 size={16} className="animate-spin" /> Sending…
            </>
          ) : (
            <>
              Request Free Counselling <ArrowRight size={16} />
            </>
          )}
        </button>

        <div className={`flex items-center gap-2 text-[11px] ${subColor} pt-1`}>
          <CheckCircle2 size={12} style={{ color: accent }} />
          <span>100% free · No obligation · Direct call from a senior counsellor</span>
        </div>
      </form>
    </div>
  );
};

export default CountryLeadForm;
